<script setup lang="ts">
import { computed, toRef, type PropType } from 'vue';
import RadioButton from 'primevue/radiobutton';
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import ToggleSwitch from 'primevue/toggleswitch';
import FloatLabel from 'primevue/floatlabel';
import Select from 'primevue/select';
import FileUpload, { type FileUploadUploaderEvent } from 'primevue/fileupload';
import { ButtonType, CustomButton, RichInput, Spacer } from '@/components';
import {
  type FormField,
  FormFieldChoice,
  type FileConfig,
  FormFieldType,
  DateMode
} from '@/models';
import { useFormBuilder } from '@/stores/formBuilder';
import { getFormattedDateList } from '@/utils';
import InputNumber from 'primevue/inputnumber';

const formBuilderStore = useFormBuilder();

const props = defineProps({
  formField: {
    type: Object as PropType<FormField>,
    required: true
  }
});

const field = toRef<FormField>(props.formField);

const handleDeleteChoice = (choice: FormFieldChoice) => {
  field.value.options.choices = field.value.options.choices?.filter(
    choiceItem => choiceItem.id !== choice.id
  );
};

const handleAddChoice = () => {
  if (field.value.options.choices) {
    const maxId = field.value.options.choices.reduce(
      (max, choice) => Math.max(max, choice.id),
      -Infinity
    );
    const maxValue = field.value.options.choices.reduce((max, choice) => {
      const numValue =
        typeof choice.value === 'number' ?
          choice.value :
          parseFloat(choice.value as string);
      return !isNaN(numValue) ? Math.max(max, numValue) : 1000;
    }, -Infinity);

    field.value.options.choices?.push({
      id: maxId! + 1,
      label: 'New choice label',
      value: maxValue! + 1
    });
  } else {
    field.value.options.choices = [];
    field.value.options.choices?.push({
      id: 1,
      label: 'New choice label',
      value: 1
    });
  }
};

const getSliderOptions = computed(() => {
  const range = field.value.options.endValue! - field.value.options.startValue!;
  return Array.from({ length: range }, (_, i) => i + 1).filter(
    divisor => range % divisor === 0
  );
});

const emits = defineEmits([
  'onRecodeValue',
  'onEditDropdownChoices',
  'onEditMatrixTable',
  'onChildFieldClick'
]);
const onRecodeValue = () => {
  emits('onRecodeValue');
};

const onEditDropdownChoices = () => {
  emits('onEditDropdownChoices');
};

const onEditMatrixTable = () => {
  emits('onEditMatrixTable');
};

const onChildFieldClick = (item: FormField) => {
  emits('onChildFieldClick', item);
};

const customUploader = async (event: FileUploadUploaderEvent) => {
  const files = event.files as File[];
  await formBuilderStore.getSignedUrl(files[0], field.value);
};

const onDeleteDrilldownCsv = () => {
  field.value.options.file = undefined;
  field.value.options.drillLevels = undefined;
};

const sectionFields = computed(() => {
  return formBuilderStore.form?.items?.filter(f => field.value.options.fields?.includes(f.identifier));
});
</script>

<template>
  <div class="form-field-config">
    <!-- CHOICE and DROPDOWN FieldType -->
    <section class="choice-type-selection"
      v-if="
        field.type === FormFieldType.CHOICE ||
        field.type === FormFieldType.DROPDOWN
      ">
      <h4 class="title choice-type-selection-title">Type of selection</h4>
      <div class="choice-single-div">
        <RadioButton v-model.number="field.options.maximumChoicesCount"
          name="single"
          :value="1"/>
        <label for="single" class="ml-2">Single</label>
      </div>
      <div class="choice-multiple-div">
        <RadioButton v-model.number="field.options.maximumChoicesCount"
          name="multiple"
          :value="2"/>
        <label for="multiple" class="ml-2">Multiple</label>
      </div>
    </section>

    <Spacer height="16"
      width="100%"
      v-if="
        field.type === FormFieldType.CHOICE ||
        field.type === FormFieldType.DROPDOWN
      "/>

    <!-- CHOICE and DROPDOWN FieldType -->
    <section class="choice-list-section"
      v-if="
        field.type === FormFieldType.CHOICE ||
        field.type === FormFieldType.DROPDOWN
      ">
      <div class="choice-list-header">
        <h4 class="title">Choices</h4>
        <CustomButton text="Record values"
          @onClick="onRecodeValue"
          :icon="{ style: 'fal', icon: 'gear' }"
          class="edit-settings-button"
          :type="ButtonType.WITHOUT_OUTLINE"/>
      </div>
      <div class="choices-list" v-if="field.type === FormFieldType.CHOICE">
        <div v-for="(choice, index) in field.options.choices"
          :key="choice.value">
          <Spacer height="4" width="100%" v-if="index > 0" />
          <div class="choice-item">
            <RichInput v-model="choice.label"
              :suggestions="formBuilderStore.form!.items"
              placeholder="Enter a choice here..."/>
            <Button icon="pi pi-trash"
              outlined
              @click="handleDeleteChoice(choice)"
              class="choice-delete"
              v-ripple/>
          </div>
        </div>
        <div class="choice-list-add">
          <CustomButton text="Add choice"
            :icon="{ style: 'fas', icon: 'plus' }"
            @onClick="handleAddChoice"
            :type="ButtonType.OUTLINED_PRIMARY"/>
        </div>
      </div>
      <div class="dropdown-choices-list"
        v-if="field.type === FormFieldType.DROPDOWN">
        <div class="dropdown-choice-length">
          {{ field.options.choices?.length }} choices in list
        </div>
        <CustomButton text="Edit list"
          :icon="{ style: 'far', icon: 'pencil' }"
          class="edit-settings-button"
          :marginLeft="16"
          @onClick="onEditDropdownChoices"
          :type="ButtonType.PRIMARY_WITHOUT_OUTLINE"/>
      </div>
    </section>

    <!-- DATE FieldType -->
    <section class="date-type-config" v-if="field.type === FormFieldType.DATE">
      <h4 class="title">Type</h4>
      <div class="date-config date">
        <RadioButton v-model="field.options.mode"
          name="date"
          :value="DateMode.DATE"/>
        <label for="date" class="ml-2">Date</label>
      </div>
      <div class="date-config time">
        <RadioButton v-model="field.options.mode"
          name="time"
          :value="DateMode.TIME"/>
        <label for="time" class="ml-2">Time</label>
      </div>
      <div class="date-config date-time">
        <RadioButton v-model="field.options.mode"
          name="datetime"
          :value="DateMode.DATE_TIME"/>
        <label for="datetime" class="ml-2">Date & Time</label>
      </div>
      <h4 class="title format" v-if="field.options.mode !== DateMode.TIME">
        Format
      </h4>
      <Select v-model="field.options.format"
        :options="getFormattedDateList()"
        optionLabel="label"
        append-to="self"
        v-if="field.options.mode !== DateMode.TIME"
        optionGroupLabel="label"
        optionGroupChildren="items"
        optionValue="value"
        showClear
        placeholder="Select a format"/>
      <div class="time-format-container"
        v-if="
          field.options.mode === DateMode.DATE_TIME ||
          field.options.mode === DateMode.TIME
        ">
        <h4 class="title format">Time Format</h4>
        <div class="time-format">
          <div class="date-config 12-hour">
            <RadioButton v-model="field.options.timeFormat"
              name="12-hour"
              :value="12"/>
            <label for="time" class="ml-2">12-Hour</label>
          </div>
          <div class="date-config 24-hour">
            <RadioButton v-model="field.options.timeFormat"
              name="24-hour"
              :value="24"/>
            <label for="time" class="ml-2">24-Hour</label>
          </div>
        </div>
      </div>
    </section>

    <!-- FILE FieldType -->
    <section class="file-type-config" v-if="field.type === FormFieldType.FILE">
      <h4 class="title">Configuration</h4>
      <div class="file-config-row">
        <div class="file-config-switch">
          <ToggleSwitch v-model="(field.options.maximumSize as FileConfig).enabled"/>
          <label class="ml-2">Set maximum file size (MB)</label>
        </div>
        <InputNumber v-model.number="(field.options.maximumSize as FileConfig).value as number"
          type="number"
          class="file-config-input"
          placeholder="Size in MB"
          :disabled="!(field.options.maximumSize as FileConfig).enabled"/>
      </div>
      <div class="file-config-row">
        <div class="file-config-switch">
          <ToggleSwitch v-model="(field.options.allowedExtensions as FileConfig).enabled"/>
          <label class="ml-2">Set allowed file extensions</label>
        </div>
        <InputText v-model="(field.options.allowedExtensions as FileConfig).value"
          type="text"
          class="file-config-input"
          placeholder=".jpg, .xlsx"
          :disabled="!(field.options.allowedExtensions as FileConfig).enabled"
          v-tooltip="
            `Specify the file extensions that you want to enforce as a comma separated list of values. 
          Each extension must begin with a dot character.
          \nFor example:\n.jpeg, .jpg, .png, .gif`
          "/>
      </div>
      <div class="file-config-row">
        <div class="file-config-switch">
          <ToggleSwitch v-model="(field.options.maximumFileNumber as FileConfig).enabled"/>
          <label class="ml-2">Set maximum number of files</label>
        </div>
        <InputNumber v-model.number="(field.options.maximumFileNumber as FileConfig).value as number"
          class="file-config-input"
          placeholder="Number of files"
          :disabled="!(field.options.maximumFileNumber as FileConfig).enabled"/>
      </div>
    </section>

    <!-- SLIDER FieldType -->
    <section class="slider-type-config"
      v-if="field.type === FormFieldType.SLIDER">
      <h4 class="title">Configuration</h4>
      <FloatLabel class="slider-config-row">
        <label for="startValue">Start value</label>
        <InputNumber id="startValue"
          v-model.number="field.options.startValue"
          class="slider-config-input"/>
      </FloatLabel>
      <FloatLabel class="slider-config-row">
        <label for="startLabel">Start label</label>
        <InputText id="startLabel"
          type="text"
          v-model="field.options.startLabel"
          class="slider-config-input"/>
      </FloatLabel>
      <FloatLabel class="slider-config-row">
        <label for="endValue">End value</label>
        <InputNumber id="endValue"
          v-model.number="field.options.endValue"
          class="slider-config-input"/>
      </FloatLabel>
      <FloatLabel class="slider-config-row">
        <label for="endLabel">End label</label>
        <InputText id="endLabel"
          type="text"
          v-model="field.options.endLabel"
          class="slider-config-input"/>
      </FloatLabel>
      <FloatLabel class="slider-config-row">
        <Select v-model.number="field.options.incrementSize"
          :options="getSliderOptions"/>
        <label for="increment-select">Increment size</label>
      </FloatLabel>
    </section>

    <!-- SIGNATURE FieldType -->
    <section class="signature-type-config"
      v-if="field.type === FormFieldType.SIGNATURE">
      <h4 class="title">Configuration</h4>
      <div class="signature-signatories">
        <label for="signatories" class="ml-2">Amount of signatories required</label>
        <InputText v-model="field.options.numberOfSignatories"
          id="signatories"
          type="number"
          class="signature-config-input"/>
      </div>
      <div class="allow-edit-toggle">
        <div class="allow-edit-title">
          Allow signatory to edit other fields in this form
        </div>
        <ToggleSwitch v-model="field.options.allowEdit" />
      </div>
    </section>

    <!-- Matrix FieldType -->
    <section class="matrix-type-config"
      v-if="field.type === FormFieldType.MATRIX">
      <div class="matrix-config-title">
        <h4 class="title">Current configuration</h4>
        <CustomButton text="Edit table"
          :icon="{ style: 'fal', icon: 'gear' }"
          class="edit-table-button"
          @onClick="onEditMatrixTable"
          :type="ButtonType.WITHOUT_OUTLINE"/>
      </div>
      <Spacer height="16" />
      <div class="matrix-config-row">
        Columns: {{ field.options.columns?.length }}
      </div>
      <div class="matrix-config-row">
        Rows: {{ field.options.rows?.length }}
      </div>
    </section>

    <!-- Drilldown FieldType -->
    <section class="drilldown-type-config"
      v-if="field.type === FormFieldType.DRILLDOWN">
      <div class="drilldown-config-upload" v-if="!field.options.drillLevels">
        <FileUpload @uploader="customUploader"
          :multiple="false"
          :auto="true"
          :custom-upload="true"
          :showUploadButton="false"
          :showCancelButton="false"
          accept="text/csv"
          mode="advanced"
          :maxFileSize="10000000">
          <template #empty>
            <div>Drag and drop drilldown csv file.</div>
          </template>
        </FileUpload>
      </div>
      <div class="drilldown-config-uploaded" v-if="field.options.drillLevels">
        <div class="drilldown-label">List</div>
        <div v-for="level in field.options.drillLevels"
          :key="level.order"
          class="drilldown-row">
          <div class="drilldown-label">{{ level.label }}</div>
          <div class="drilldown-records">{{ level.data.length }}</div>
        </div>

        <div class="csv-file-container">
          <div class="csv-file">
            <FontAwesomeIcon :icon="[`fas`, `file-csv`]" class="icon" />
            <div class="file-name">{{ field.options.blob?.name }}</div>
            <CustomButton text="Delete CSV"
              :icon="{ style: 'fal', icon: 'trash' }"
              class="delete-csv-button"
              :marginLeft="16"
              @onClick="onDeleteDrilldownCsv"
              :type="ButtonType.OUTLINED"/>
          </div>
        </div>
      </div>
    </section>

    <!-- Section FieldType -->
    <section class="section-type-config" v-if="field.type === FormFieldType.SECTION">
      <div class="collapse-initially-container">
        <Checkbox v-model="field.options.collapsedInitially"
            inputId="collapse-initially"
            name="collapse-initially"
            class="collapse-initially"
            :binary="true"/>
        <div class="group-header">Collapse section initially</div>
      </div>
      <h3 class="child-fields-label">Section sub fields</h3>
      <div class="children" v-for="field in sectionFields" :key="field.identifier">
        <div class="child-item" @click="() => onChildFieldClick(field)">{{ field.label }}</div>
      </div>
    </section>
  </div>
</template>

<style scoped lang="scss">
.form-field-config {
  width: 100%;
  padding: 0 4px;
  min-width: 0; 
}
.title {
  font-weight: 700;
}
.choice-type-selection,
.date-type-config {
  .choice-single-div,
  .choice-multiple-div,
  .date-config {
    display: flex;
    align-items: center;
    margin-top: 8px;

    label {
      margin-left: 16px;
    }
  }
}
.choice-list-section {
  .choice-list-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .choice-item {
    display: flex;
    align-items: center;

    .choice-delete {
      visibility: hidden;
      margin-left: 16px;
      border: 1px solid transparent;
    }

    &:hover {
      .choice-delete {
        visibility: visible;
        border: 1px solid transparent;
      }
    }
  }

  .choice-list-add {
    margin-top: 12px;
  }

  .dropdown-choices-list {
    display: flex;
    align-items: center;
  }
}
.format {
  margin: 24px 0px 8px;
}
:deep(.p-select .p-select-overlay) {
  min-width: 250px;
}
:deep(.p-select-option-group-label) {
  font-weight: 700;
  color: black;
}
.file-type-config {
  .file-config-row {
    display: flex;
    align-items: center;
    margin-top: 8px;
    width: 450px;
    justify-content: space-between;

    .file-config-switch {
      display: flex;
      align-items: center;

      label {
        margin-left: 8px;
      }
    }

    .file-config-input {
      font-size: 14px;
    }
    :deep(::placeholder) {
      color: #888;
      font-style: italic;
    }
  }
}

.slider-type-config {
  .slider-config-row {
    display: flex;
    flex-direction: column;
    margin-top: 32px;

    .slider-config-input {
      font-size: 14px;
    }
  }
}

.signature-type-config {
  .signature-signatories {
    margin-top: 8px;
    display: flex;
    align-items: center;

    .signature-config-input {
      margin-left: 8px;
      width: 80px;
    }
  }

  .allow-edit-toggle {
    margin-top: 8px;
    display: flex;
    align-items: center;

    .allow-edit-title {
      margin-right: 16px;
    }
  }
}

.matrix-type-config {
  .matrix-config-row {
    font-weight: 600;
  }

  .matrix-config-title {
    display: flex;
    align-items: center;

    h4 {
      margin-right: 36px;
    }
  }
}

.drilldown-type-config {
  .drilldown-label {
    font-weight: 600;
  }

  .drilldown-row {
    display: flex;
    justify-items: center;

    .drilldown-records {
      margin-left: 16px;
    }
  }

  .csv-file-container {
    display: inline-block;

    .csv-file {
      display: flex;
      align-items: center;
      margin-top: 16px;
      padding: 8px 12px;
      border: 1px solid #ced3d9;
      border-radius: 4px;
      background-color: #fafbfc;

      .file-name {
        margin-left: 8px;
      }
    }
  }
}
.child-fields-label {
  font-weight: 700;
}
.children {
  display: flex;
  flex-direction: column;
}

.child-item {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border: 1px solid #ced3d9;
  border-radius: 4px;
  padding: 8px 12px;
  margin-top: 8px;

  &:hover {
    background-color: #f5f5f5;
    cursor: pointer;
  }
}

.collapse-initially-container {
  display: flex;
  align-items: center;
  margin-bottom: 16px;

  .collapse-initially {
    margin-right: 8px;
  } 
}
</style>
